// v1
// Path: frontend/src/components/SyncHub/shared/PresetAccordionItem.jsx
// Inputs: preset, isExpanded, onToggleExpand, token, sourceSystem, targetSystem, availableTables, targetEntities, onSavePreset, onDuplicatePreset, onRenamePreset, onDeletePreset, onResetPresetDefaults
// Outputs: Tier 2 full-width expandable Preset accordion row with strict 3-column Table/Entity selector row, powered active glow badges, and embedded MappingDesignerTable.
// Summary: Self-contained accordion item for individual pipeline presets within a standard set, providing single-item expansion, dynamic schema switching, and pixel-aligned 3-column ETL mapping canvas.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SearchableSelect from '../SearchableSelect';
import MappingDesignerTable from './MappingDesignerTable';
import { getQbEntityMeta, normalizeEntityName } from '../entityCatalogs';

// Auto-injected unbox helper for standardized API responses
const unbox = async (res) => {
    const payload = await res.json();
    if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
        return payload.data;
    }
    return payload;
};

// Helper to merge target fields with existing preset mappings
const mergeTargetFieldsWithMappings = (tFields, rawMappings) => {
    const allTargets = [...(tFields || [])];
    const existingMap = new Map();
    (rawMappings || []).forEach(m => {
        if (m && m.target) {
            existingMap.set(m.target.toLowerCase(), {
                target: m.target,
                sources: Array.isArray(m.sources) && m.sources.length > 0
                    ? m.sources
                    : (m.source ? (m.source.includes('+') ? m.source.split('+').map(s => s.trim()) : [m.source]) : []),
                source: m.source || (Array.isArray(m.sources) ? m.sources.join(m.delimiter !== undefined ? m.delimiter : ' - ') : ''),
                delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                template: m.template || ''
            });
        }
    });

    const result = [];
    for (const tf of allTargets) {
        const exist = existingMap.get(tf.name.toLowerCase());
        if (exist) {
            result.push(exist);
            existingMap.delete(tf.name.toLowerCase());
        } else {
            result.push({
                target: tf.name,
                sources: [],
                source: '',
                delimiter: ' - ',
                template: ''
            });
        }
    }

    for (const [_, custom] of existingMap.entries()) {
        result.push(custom);
    }
    return result;
};

export default function PresetAccordionItem({
    preset,
    isExpanded = false,
    onToggleExpand = () => {},
    token,
    sourceSystem = 'Passport',
    targetSystem = 'QuickBooks',
    availableTables = [],
    targetEntities = [],
    onSavePreset = async () => {},
    onDuplicatePreset = () => {},
    onRenamePreset = () => {},
    onDeletePreset = () => {},
    onResetPresetDefaults = () => {}
}) {
    // Current entity and table for this preset pipeline
    const canonicalEntity = normalizeEntityName(preset.TargetQBEntity || preset.EntityType || 'Customer');
    const entityMeta = getQbEntityMeta(canonicalEntity);
    const defaultTable = preset.SourceTable || entityMeta?.defaultTable || '';

    const [selectedTable, setSelectedTable] = useState(defaultTable);
    const [selectedEntity, setSelectedEntity] = useState(canonicalEntity);
    const [definitions, setDefinitions] = useState(null);
    const [mappings, setMappings] = useState([]);
    const [loadingDefinitions, setLoadingDefinitions] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);

    // Custom mapping creation state
    const [newTarget, setNewTarget] = useState('');
    const [newSource, setNewSource] = useState('');

    // Sync state if preset updates from parent
    useEffect(() => {
        setSelectedTable(preset.SourceTable || entityMeta?.defaultTable || '');
        setSelectedEntity(canonicalEntity);
    }, [preset.SourceTable, canonicalEntity, entityMeta?.defaultTable]);

    // Fetch field definitions when expanded and table/entity changes
    const fetchDefinitions = useCallback(async () => {
        if (!isExpanded) return;
        setLoadingDefinitions(true);
        try {
            const activeTbl = selectedTable || preset.SourceTable || '';
            const params = new URLSearchParams({
                entityType: selectedEntity,
                sourceDatabaseName: 'pbsdata00',
                tableName: activeTbl
            });

            const res = await fetch(`/api/sync/field-definitions/${encodeURIComponent(selectedEntity)}?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await unbox(res);
                setDefinitions(data);

                let initialRaw = Array.isArray(preset.mappings) ? preset.mappings : [];
                if ((!initialRaw || initialRaw.length === 0) && preset.FieldMappingsJson) {
                    try { initialRaw = JSON.parse(preset.FieldMappingsJson); } catch(e) {}
                }

                const targetFields = data.targetFields || [];
                const merged = mergeTargetFieldsWithMappings(targetFields, initialRaw);
                setMappings(merged);
                setIsDirty(false);
            }
        } catch (err) {
            console.error('[PresetAccordionItem] Error loading definitions:', err);
        } finally {
            setLoadingDefinitions(false);
        }
    }, [isExpanded, selectedTable, selectedEntity, preset.SourceTable, preset.mappings, preset.FieldMappingsJson, token]);

    useEffect(() => {
        if (isExpanded) {
            fetchDefinitions();
        }
    }, [isExpanded, fetchDefinitions]);

    // Compute live mapping coverage count
    const totalFields = definitions?.targetFields?.length || mappings.length || (Array.isArray(preset.mappings) ? preset.mappings.length : 0) || 0;
    const mappedFieldsCount = useMemo(() => {
        if (isExpanded && mappings.length > 0) {
            return mappings.filter(m => {
                const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
                return s.length > 0 && s.some(x => x && x.trim().length > 0);
            }).length;
        }
        const raw = Array.isArray(preset.mappings) ? preset.mappings : [];
        return raw.filter(m => {
            const s = Array.isArray(m.sources) ? m.sources : (m.source ? [m.source] : []);
            return s.length > 0 && s.some(x => x && x.trim().length > 0);
        }).length;
    }, [isExpanded, mappings, preset.mappings]);

    // Mutate field mappings
    const handleCustomMappingChange = (targetName, field, val) => {
        setIsDirty(true);
        setMappings(prev => prev.map(m => {
            if (m.target.toLowerCase() === targetName.toLowerCase()) {
                const updated = { ...m, [field]: val };
                if (field === 'sources') {
                    const delim = m.delimiter !== undefined ? m.delimiter : ' - ';
                    updated.source = Array.isArray(val) ? val.join(delim) : String(val);
                }
                return updated;
            }
            return m;
        }));
    };

    const handleRemoveCustomRow = (targetName) => {
        setIsDirty(true);
        setMappings(prev => prev.filter(m => m.target.toLowerCase() !== targetName.toLowerCase()));
    };

    const handleAddCustomMapping = () => {
        if (!newTarget.trim() || !newSource.trim()) return;
        const exists = mappings.some(m => m.target.toLowerCase() === newTarget.trim().toLowerCase());
        if (exists) return;

        const newEntry = {
            target: newTarget.trim(),
            sources: [newSource.trim()],
            source: newSource.trim(),
            delimiter: ' - ',
            template: ''
        };
        setMappings(prev => [newEntry, ...prev]);
        setIsDirty(true);
        setNewTarget('');
        setNewSource('');
    };

    // Save mappings for this preset
    const handleSave = async () => {
        setSaving(true);
        setSaveMessage(null);
        try {
            const cleanMappings = mappings
                .filter(m => m.target && ((Array.isArray(m.sources) && m.sources.length > 0) || (m.source && m.source.trim().length > 0)))
                .map(m => ({
                    target: m.target.trim(),
                    source: m.source ? m.source.trim() : (m.sources ? m.sources.join(m.delimiter || ' - ') : ''),
                    sources: Array.isArray(m.sources) && m.sources.length > 0 ? m.sources : [(m.source || '').trim()],
                    delimiter: m.delimiter !== undefined ? m.delimiter : ' - ',
                    template: m.template || undefined
                }));

            await onSavePreset({
                mappingId: preset.MappingID,
                setName: preset.SetName,
                sourceSystem: preset.SourceSystem || sourceSystem,
                targetSystem: preset.TargetSystem || targetSystem,
                entityType: selectedEntity,
                targetQBEntity: selectedEntity,
                sourceTable: selectedTable,
                mappingName: preset.MappingName,
                mappings: cleanMappings
            });

            setIsDirty(false);
            setSaveMessage({ type: 'success', text: 'Preset pipeline saved!' });
            setTimeout(() => setSaveMessage(null), 3000);
        } catch (err) {
            setSaveMessage({ type: 'error', text: err.message || 'Save failed' });
        } finally {
            setSaving(false);
        }
    };

    // Prepare table dropdown options with column counts
    const tableOptions = useMemo(() => {
        return availableTables.map(t => {
            const tName = typeof t === 'string' ? t : (t.name || t.TableName || '');
            const fCount = t.fieldCount || t.FieldCount || (t.columns ? t.columns.length : 0);
            const countStr = fCount > 0 ? ` (${fCount} cols)` : '';
            return {
                value: tName,
                label: `${tName}${countStr}`,
                description: t.description || t.Description || undefined,
                isPowered: tName.toUpperCase() === (selectedTable || '').toUpperCase() && mappedFieldsCount > 0
            };
        });
    }, [availableTables, selectedTable, mappedFieldsCount]);

    // Prepare entity dropdown options with field counts & powered active state
    const entityOptions = useMemo(() => {
        return targetEntities.map(ent => {
            const eName = typeof ent === 'string' ? ent : (ent.entity || ent.name);
            const eLabel = typeof ent === 'string' ? ent : (ent.label || ent.name || ent.entity);
            const fCount = ent.fieldCount || 36; // canonical fallback
            const isCurrent = eName.toLowerCase() === selectedEntity.toLowerCase();
            const isPowered = isCurrent ? mappedFieldsCount > 0 : false;
            return {
                value: eName,
                label: `${eLabel} (${fCount} flds)`,
                isPowered
            };
        });
    }, [targetEntities, selectedEntity, mappedFieldsCount]);

    return (
        <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-900/90 shadow-sm transition-all">
            {/* ══════════════════════════════════════════════════════════════════════ */}
            {/* TIER 2: FULL-WIDTH PRESET LIST ROW                                     */}
            {/* ══════════════════════════════════════════════════════════════════════ */}
            <div
                onClick={onToggleExpand}
                className="w-full px-4 py-3 bg-slate-850 hover:bg-slate-800 flex items-center justify-between gap-4 cursor-pointer transition-colors border-b border-slate-800/80"
            >
                {/* Left: Expand Chevron & Preset Name */}
                <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`text-slate-400 text-xs transition-transform duration-200 ${isExpanded ? 'rotate-90 text-indigo-400' : ''}`}>
                        ▶
                    </span>
                    <span className="material-symbols-outlined text-indigo-400 text-[18px]">account_tree</span>
                    <div className="flex items-center gap-2 truncate">
                        <span className="font-bold text-sm text-white tracking-wide truncate">
                            {preset.MappingName || `${selectedEntity} Pipeline`}
                        </span>
                        <span className="text-[11px] text-slate-400 font-mono hidden sm:inline truncate">
                            ({selectedTable || 'No table'} ➔ {selectedEntity})
                        </span>
                    </div>
                </div>

                {/* Right: Mapping Coverage Preview & Actions */}
                <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
                    {/* Coverage badge */}
                    <span
                        className={`px-2.5 py-0.5 rounded-full text-[11px] font-mono font-semibold border flex items-center gap-1.5 shadow-sm ${
                            mappedFieldsCount > 0
                                ? 'bg-emerald-950/80 text-emerald-300 border-emerald-700/80 shadow-[0_0_8px_rgba(16,185,129,0.2)]'
                                : 'bg-slate-800 text-slate-400 border-slate-700'
                        }`}
                        title="Mapped destination fields"
                    >
                        {mappedFieldsCount > 0 && (
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_4px_#34d399]" />
                        )}
                        <span>{mappedFieldsCount} / {totalFields || 36} fields mapped</span>
                    </span>

                    {/* Action Buttons */}
                    <button
                        type="button"
                        onClick={() => onDuplicatePreset(preset)}
                        className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded text-xs font-medium border border-slate-700 transition-colors"
                        title="Duplicate this pipeline preset"
                    >
                        Duplicate
                    </button>
                    <button
                        type="button"
                        onClick={() => onRenamePreset(preset)}
                        className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded text-xs font-medium border border-slate-700 transition-colors"
                        title="Rename pipeline preset"
                    >
                        Rename
                    </button>
                    <button
                        type="button"
                        onClick={() => onDeletePreset(preset)}
                        className="px-2 py-1 bg-slate-800 hover:bg-rose-950/60 text-slate-400 hover:text-rose-300 rounded text-xs font-medium border border-slate-700 hover:border-rose-800/60 transition-colors"
                        title="Delete pipeline preset"
                    >
                        Delete
                    </button>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════ */}
            {/* TIER 2 EXPANDED CONTENT                                                */}
            {/* ══════════════════════════════════════════════════════════════════════ */}
            {isExpanded && (
                <div className="p-4 space-y-4 bg-slate-900 border-t border-slate-800">
                    {/* TABLE / ENTITY SELECTOR ROW (Strict 3 Columns: 1fr - 200px - 1fr) */}
                    <div className="grid grid-cols-[minmax(0,1fr)_200px_minmax(0,1fr)] gap-4 items-center bg-slate-950/70 p-3 rounded-xl border border-slate-800 shadow-inner">
                        {/* Left (1fr): ERP Source Table Searchable Select */}
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 shrink-0">
                                <span className="material-symbols-outlined text-[15px] text-sky-400">table_chart</span>
                                ERP Source:
                            </span>
                            <SearchableSelect
                                options={tableOptions}
                                value={selectedTable}
                                onChange={val => {
                                    setSelectedTable((val || '').toUpperCase());
                                    setIsDirty(true);
                                }}
                                placeholder="Search ERP Source Table..."
                                allowCustom={true}
                                className="w-full"
                            />
                        </div>

                        {/* Center (200px): Flow Progress Metrics */}
                        <div className="flex items-center justify-center gap-1.5 text-xs font-mono font-bold text-emerald-400 text-center shrink-0">
                            <span>➔</span>
                            <span className="px-2 py-0.5 rounded bg-emerald-950/60 border border-emerald-800/60 text-[11px] truncate shadow-sm">
                                {mappedFieldsCount} / {totalFields || 36} mapped
                            </span>
                            <span>➔</span>
                        </div>

                        {/* Right (1fr): Target Entity Searchable Select */}
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 shrink-0">
                                <span className="material-symbols-outlined text-[15px] text-purple-400">category</span>
                                Target Entity:
                            </span>
                            <SearchableSelect
                                options={entityOptions}
                                value={selectedEntity}
                                onChange={val => {
                                    setSelectedEntity(val);
                                    setIsDirty(true);
                                }}
                                placeholder="Search Target Entity..."
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Notification message */}
                    {saveMessage && (
                        <div className={`px-4 py-2 rounded-lg text-xs flex items-center justify-between gap-2 border ${
                            saveMessage.type === 'success'
                                ? 'bg-emerald-950/80 border-emerald-800 text-emerald-300'
                                : 'bg-rose-950/80 border-rose-800 text-rose-300'
                        }`}>
                            <span>{saveMessage.text}</span>
                            <button type="button" onClick={() => setSaveMessage(null)} className="text-slate-400 hover:text-white">&times;</button>
                        </div>
                    )}

                    {/* FIELD MAPPING CANVAS (Embedded MappingDesignerTable with 3-Column Grid) */}
                    <div className="border border-slate-800 rounded-xl overflow-hidden shadow-md bg-slate-900 h-[520px] flex flex-col">
                        {loadingDefinitions ? (
                            <div className="flex items-center justify-center h-full text-slate-400 text-xs">
                                <span className="material-symbols-outlined animate-spin text-2xl mr-2 text-indigo-400">sync</span>
                                Loading pipeline field schemas...
                            </div>
                        ) : (
                            <MappingDesignerTable
                                isStandardMode={true}
                                selectedQBEntity={selectedEntity}
                                activeTableName={selectedTable}
                                mappings={mappings}
                                definitions={definitions}
                                sourceColumns={definitions?.sourceFields || []}
                                newTarget={newTarget}
                                setNewTarget={setNewTarget}
                                newSource={newSource}
                                setNewSource={setNewSource}
                                handleAddCustomMapping={handleAddCustomMapping}
                                handleCustomMappingChange={handleCustomMappingChange}
                                handleRemoveCustomRow={handleRemoveCustomRow}
                            />
                        )}
                    </div>

                    {/* Footer Actions: Reset Defaults & Save Mappings */}
                    <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-800">
                        <button
                            type="button"
                            onClick={() => onResetPresetDefaults(preset)}
                            disabled={saving}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-750 text-slate-300 hover:text-white border border-slate-700 text-xs font-semibold rounded-lg transition-colors shadow-sm"
                        >
                            Reset Defaults
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className={`px-4 py-1.5 text-xs font-semibold rounded-lg shadow transition-colors flex items-center gap-1.5 ${
                                isDirty
                                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                                    : 'bg-slate-800 text-slate-300 border border-slate-700 hover:text-white'
                            } disabled:opacity-50`}
                        >
                            {saving ? (
                                <>
                                    <span className="material-symbols-outlined text-[14px] animate-spin">sync</span>
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-[15px]">save</span>
                                    <span>Save Mappings</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
